<script setup>
/**
 * 按模型能力 + 模型：横向条形，烧钱最多的在最上面。**点一根条 = 下钻到这个模型的调用日志**。
 *
 * 横向而不是竖向：类目名是 `deepseek / deepseek-reasoner` 这种长串，竖着放要么斜排要么截断。
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EChart from '../../../components/EChart.vue'
import { categoryAxis, chartBase, chartTheme, valueAxis } from '../../../components/chart-theme.js'
import { useAdminTheme } from '../../../composables/useAdminTheme.js'
import { formatInt, formatMoney } from '../llm-format.js'
import { modelLabelOf } from '../cost-metrics.js'

const props = defineProps({
  /** @type {import('vue').PropType<Array<{capability:string,provider:string,provider_display_name:string,provider_capability_code:string,model:string,supplier_cost_micros:number,calls:number,charged_points:number,unpriced_calls:number}>>} */
  items: { type: Array, required: true },
})

const emit = defineEmits(['select'])

const { t } = useI18n()
// 主题读进 computed：切明暗时 option 才会重算（ECharts 不认 CSS 变量）。
const { theme: themeName } = useAdminTheme()

/** ECharts 的类目轴从下往上排，所以倒过来喂：成本最高的落在最上面一行。 */
const rows = computed(() => [...props.items].reverse())

const option = computed(() => {
  const theme = chartTheme(themeName.value)
  return {
    ...chartBase(theme),
    grid: { top: 8, left: 8, right: 72, bottom: 8, containLabel: true },
    // 成本轴一律是钱：账本单位（107）印在轴上，运营不知道那是多少钱。
    xAxis: { ...valueAxis(theme), axisLabel: { color: theme.textMuted, formatter: formatMoney } },
    yAxis: { ...categoryAxis(theme), data: rows.value.map(modelLabelOf) },
    tooltip: { ...chartBase(theme).tooltip, formatter: tooltipOf },
    series: [
      {
        type: 'bar',
        name: t('visualGameAdmin.llm.cost.metric.cost'),
        barMaxWidth: 22,
        itemStyle: { color: theme.bar, borderRadius: [0, 4, 4, 0] },
        // 条不多，直接把数标在条尾 —— 看的人不用把每根条都 hover 一遍。
        label: { show: true, position: 'right', color: theme.textMuted, formatter: (p) => formatMoney(p.value) },
        data: rows.value.map((item) => item.supplier_cost_micros),
      },
    ],
  }
})

/** @param {any[]} params */
function tooltipOf(params) {
  const item = rows.value[params[0].dataIndex]
  const lines = [
    `<strong>${modelLabelOf(item)}</strong>`,
    `${t('visualGameAdmin.llm.cost.metric.cost')}: ${formatMoney(item.supplier_cost_micros)}`,
    `${t('visualGameAdmin.llm.cost.metric.calls')}: ${formatInt(item.calls)}`,
    `${t('visualGameAdmin.llm.cost.metric.charged_points')}: ${formatInt(item.charged_points)}`,
  ]
  if (item.unpriced_calls > 0) {
    lines.push(`⚠ ${t('visualGameAdmin.llm.cost.unpriced.short', { count: item.unpriced_calls })}`)
  }
  lines.push(t('visualGameAdmin.llm.cost.chart.clickToCalls'))
  return lines.join('<br/>')
}

function onSelect({ index }) {
  emit('select', rows.value[index])
}
</script>

<template>
  <EChart
    :option="option"
    :height="`${Math.max(200, rows.length * 44 + 40)}px`"
    @select="onSelect"
  />
</template>
